/**
 * SampleSpecs
 *
 * Zero API calls — completely static. An inline, horizontally-scrollable row
 * of sample OpenAPI specs shown right under the input box so users without a
 * spec can reach first-success in one click.
 *
 * Each chip shows only an auth icon (globe = public, lock = needs auth) — no
 * initials, no text labels. Only the chip strip scrolls left/right; the page
 * itself never scrolls horizontally.
 *
 * Usage:
 *   <SampleSpecs onSelect={(url) => handleUrlFetch(url)} loadingUrl={loadingSampleUrl} />
 */

import { Lock, Globe } from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

type AuthBadge = "oauth" | "api-key" | "public";

type SampleSpec = {
  name: string;
  specUrl: string;
  auth: AuthBadge;
};

// ── Data ──────────────────────────────────────────────────────────────────────

const SAMPLES: SampleSpec[] = [
  {
    name: "Petstore",
    specUrl: "https://petstore3.swagger.io/api/v3/openapi.json",
    auth: "public",
  },
  {
    name: "Stripe",
    specUrl: "https://api.apis.guru/v2/specs/stripe.com/2022-11-15/openapi.yaml",
    auth: "api-key",
  },
  {
    name: "GitHub",
    specUrl: "https://api.apis.guru/v2/specs/github.com/v0.1/openapi.yaml",
    auth: "oauth",
  },
  {
    name: "Notion",
    specUrl: "https://api.apis.guru/v2/specs/notion.com/1.0.0/openapi.yaml",
    auth: "api-key",
  },
  {
    name: "Slack",
    specUrl: "https://api.apis.guru/v2/specs/slack.com/1.7.0/openapi.yaml",
    auth: "oauth",
  },
  {
    name: "Open-Meteo",
    specUrl: "https://raw.githubusercontent.com/open-meteo/open-meteo/main/openapi.yml",
    auth: "public",
  },
  {
    name: "Exchange Rate",
    specUrl: "https://api.apis.guru/v2/specs/exchangerate-api.com/4/openapi.yaml",
    auth: "public",
  },
];

// public → globe; api-key / oauth → lock ("needs auth")
function authIcon(auth: AuthBadge) {
  if (auth === "public") {
    return <Globe className="w-3.5 h-3.5 text-muted-foreground/70 flex-shrink-0" aria-label="Public — no auth" />;
  }
  return <Lock className="w-3.5 h-3.5 text-muted-foreground/70 flex-shrink-0" aria-label="Needs auth" />;
}

// ── Component ─────────────────────────────────────────────────────────────────

type Props = {
  onSelect: (specUrl: string) => void;
  loadingUrl?: string | null;
};

export default function SampleSpecs({ onSelect, loadingUrl }: Props) {
  return (
    <section className="container max-w-2xl mx-auto px-6 mt-4">
      <div className="flex items-center gap-3">
        {/* Label — stays put, doesn't scroll */}
        <span className="text-[13px] text-muted-foreground/70 flex-shrink-0">
          No spec? Try:
        </span>

        {/* Chip strip — only this scrolls horizontally */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none -my-1 py-1">
          {SAMPLES.map((spec) => {
            const isLoading = loadingUrl === spec.specUrl;
            return (
              <button
                key={spec.specUrl}
                type="button"
                onClick={() => onSelect(spec.specUrl)}
                disabled={!!loadingUrl}
                className="group flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-background hover:bg-muted/60 hover:border-border transition-colors px-3 py-1.5 text-[13px] font-medium text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-3.5 h-3.5 border-[1.5px] border-primary/30 border-t-primary rounded-full animate-spin flex-shrink-0" />
                ) : (
                  authIcon(spec.auth)
                )}
                {spec.name}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
